import './product-sodium.scss';

import { ADD_TO_CART, DISABLED, ON, SOLD_OUT } from '../config/constants';
import { turnOffButton, turnOnButton } from '../inquire-comingsoon';
import addToCart from '../utils/addToCart';
import addClass from '../utils/dom/addClass';
import clickEvent from '../utils/dom/click';
import removeClass from '../utils/dom/removeClass';
import formatPrice, { isPriceZero } from '../utils/formatPrice';
import playVideo from '../utils/playVideo';
import preload from '../utils/preload';
import setupInquire from '../utils/setupInquire';

const isMobile = () => window.innerWidth <= 768;

let ppi;
function getPPI() {
  if (ppi) return ppi;

  const div = document.createElement('div');
  div.style.position = 'absolute';
  div.style.top = '-9999px';
  div.style.left = '-9999px';
  div.style.width = '1in';
  div.style.height = '1in';
  document.body.appendChild(div);
  ppi = div.offsetWidth;
  document.body.removeChild(div);
  // Fallback for browsers that don't support "in" or return 0
  return ppi || 96;
}

function convertMmToPx(mm) {
  return (mm / 25.4) * getPPI();
}

// Debounce utility for resize events
function debounce(fn, delay) {
  let timer = null;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

class ProductSodium extends HTMLElement {
  constructor() {
    super();
    this.hideCarouselColor = this.hideCarouselColor.bind(this);
    this.handleResize = null;
    this.initOptions = this.initOptions.bind(this);
    this.onAddToBag = this.onAddToBag.bind(this);
    this.setBackgroundHeight = this.setBackgroundHeight.bind(this);
    this.onColorChange = this.onColorChange.bind(this);
    this.onOptionChange = this.onOptionChange.bind(this);
    this.onRelatedProductChange = this.onRelatedProductChange.bind(this);
    this.onSizeChange = this.onSizeChange.bind(this);
  }

  connectedCallback() {
    const { PRODUCT, VARIANT, VARIANTS } = window;

    this.addToBagButton = this.querySelector('.add-to-bag-button');
    this.inquireButton = this.querySelector('button.inquire-button');
    this.colorSelect = this.querySelector('.product-color-select');
    this.relatedProducts = [...this.querySelectorAll('[data-related-product="true"]')];
    // Prioritize secondary products select if it exists, otherwise use the first related product
    const secondaryProductsSelect = PRODUCT?.id ? this.querySelector('#product-' + PRODUCT.id + '-secondary') : null;
    this.relatedProductId =
      secondaryProductsSelect?.value || this.relatedProducts[0]?.querySelector('select')?.value || 'none';
    this.sizes = this.querySelectorAll('.product-size-select');
    this.sizeLabels = this.querySelectorAll('.product-size');
    const labelNodes = [...this.querySelectorAll('.labels-to-resize .label')];
    this.labels = labelNodes.slice(0, labelNodes.length - 1);
    this.options = [...this.querySelectorAll('.product-multi-option-select')];

    this.color = this.colorSelect.value || VARIANTS?.[0]?.color;
    this.variants = VARIANTS?.filter((variant) => variant.color === this.color) || [];
    this.variant = this.variants.find((variant) => variant.id === VARIANT.id) || this.variants[0];

    this.handleResize = debounce(() => {
      this.setBackgroundHeight();
      this.updateLabelHeights();
    }, 100);
    window.addEventListener('resize', this.handleResize);

    this.initAddToBag();
    this.initColorSelect();
    this.initRelatedProducts();
    this.initSizes();
    this.initOptions();
    playVideo(VARIANT);

    const images = [...document.querySelectorAll('.product-featured-image-image')];
    preload(images);

    // Wait for the carousel custom element to be defined before calling hideCarouselColor
    if (window.customElements && PRODUCT?.id) {
      customElements.whenDefined('product-carousel').then(() => {
        this.hideCarouselColor();
      });
    } else {
      this.hideCarouselColor();
    }
    this.handleResize();
    setupInquire(this.variant);
    this.render();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
  }

  initAddToBag() {
    this.addToBagButton?.addEventListener(clickEvent(), this.onAddToBag);
  }

  initColorSelect() {
    if (!this.colorSelect) return;
    // Add event listener to the color select element
    this.colorSelect.addEventListener('change', this.onColorChange);
  }

  initOptions() {
    if (!this.options || this.options.length === 0) return;
    for (const option of this.options) {
      // Add event listener to each option select element
      option.addEventListener('change', this.onOptionChange);
    }
  }

  initRelatedProducts() {
    if ((this.relatedProducts?.length || 0) === 0) return;
    for (const relatedProduct of this.relatedProducts) {
      const select = relatedProduct.querySelector('select');
      select.addEventListener('change', this.onRelatedProductChange);
    }
  }

  initSizes() {
    if (!this.sizes || this.sizes.length === 0) return;
    for (const size of this.sizes) {
      // Add event listener to each size select element
      size.addEventListener('change', this.onSizeChange);
    }
  }

  async onAddToBag() {
    await addToCart(this.addToBagButton, this.variant, null, this.relatedProductId?.split('/')?.pop());
  }

  onColorChange(event) {
    const selectedColor = event.target.value;
    this.color = selectedColor;

    this.variants = window.VARIANTS.filter((variant) => variant.color === this.color);

    if (this.options && this.options.length > 0) {
      this.variant =
        this.variants.find(
          (variant) =>
            variant.options[1] === this.options[1].value.value && variant.options[2] === this.options[2]?.value,
        ) || this.variants[0];
    } else {
      this.variant = this.variants.find((variant) => variant.size === this.variant.size) || this.variants[0];
    }

    // Update the gradient background
    const gradientBackground = this.querySelector('.product-color-gradient');
    const { gradientColors, hexColor } = this.variant;
    if (gradientColors && gradientColors.length > 0) {
      gradientBackground.style.background = `linear-gradient(to right, ${gradientColors.join(', ')})`;
    } else {
      gradientBackground.style.background = hexColor;
    }

    // Update the GradientStones component
    const gradientStonesElements = this.querySelectorAll('gradient-stones');
    gradientStonesElements.forEach((element) => {
      if (typeof element.updateGradientBackground === 'function') {
        element.updateGradientBackground(gradientColors, hexColor);
      }
    });

    const additionalGradientBackground = this.querySelector('.product-color-gradient.additional');
    if (additionalGradientBackground) {
      const { colorsAdditional } = this.variant;
      if (colorsAdditional) {
        // Handle both string and array formats
        const colors = Array.isArray(colorsAdditional)
          ? colorsAdditional
          : typeof colorsAdditional === 'string'
            ? colorsAdditional.split(',')
            : [];
        additionalGradientBackground.style.background = `linear-gradient(to right, ${colors.join(', ')})`;
      }
    }

    this.render();
  }

  onOptionChange() {
    // find a variant whose option and color matches the current selected dropdown value
    const selectedOptions = [this.color]
      .concat(
        this.options.map((select) => {
          if (select.value.indexOf(': ') === -1) {
            return select.value.trim();
          }
          return select.value.split(': ')[1].trim();
        }),
      )
      .join('');
    this.variant = this.variants.find((variant) => variant.options.join('') === selectedOptions);
    this.render();
  }

  onRelatedProductChange(event) {
    const selectedValue = event.target.value;
    for (const relatedProduct of this.relatedProducts) {
      const select = relatedProduct.querySelector('select');
      select.value = selectedValue;
    }

    this.relatedProductId = selectedValue;
    this.render();
  }

  // Handle size change event
  onSizeChange(event) {
    const selectedValue = event.target.value;

    this.variant = this.variants.find((variant) => variant.size === selectedValue);

    this.render();
  }

  hideCarouselColor() {
    // Find the product carousel with the same id and hide the color
    const productCarousel = document.getElementById(`product-carousel-${window.PRODUCT.id}`);
    if (productCarousel && typeof productCarousel.hideColor === 'function') {
      productCarousel.hideColor(this.color);
    }
  }

  render() {
    const {
      available,
      comingsoon: isComingSoon,
      diamonds,
      dimensions,
      gradientColors,
      hexColor,
      id,
      material,
      media,
      price,
      releaseDate,
      sku,
      soldOut,
    } = this.variant;
    setupInquire(this.variant);

    // Update the image
    const currentImages = [...document.querySelectorAll('.product-featured-image-image.on')];
    const newImages = [...document.querySelectorAll(`#image-${id},#image-${id}-mobile`)];

    for (const currentImage of currentImages) {
      removeClass(currentImage, ON);
    }
    for (const newImage of newImages) {
      addClass(newImage, ON);
    }

    // Update the button prices
    if (this.addToBagButton) {
      const buttonPrice = this.addToBagButton.querySelector('.product-button-price');
      // Check if price is zero - handle both number and string formats
      const priceValue = typeof price === 'string' ? parseFloat(price) : price;
      const priceIsZero = !priceValue || priceValue === 0 || isNaN(priceValue);

      // Format the price (will check map first, then format if needed)
      const formattedPrice = formatPrice(price, id);
      const isFormattedPriceZero = isPriceZero(formattedPrice);

      // Debug output
      // biome-ignore lint: debugging price display
      console.log('[product-sodium] Price debugging:', {
        variantId: id,
        rawPrice: price,
        priceType: typeof price,
        priceValue,
        priceIsZero,
        formattedPrice,
        isFormattedPriceZero,
        priceInMap: window.HMR?.prices?.get(id),
      });

      // Check if the formatted price represents zero (handles all currency formats)
      const mainPriceDisplay = priceIsZero || isFormattedPriceZero ? '' : formattedPrice;

      let formattedAdditionalPrice = '';
      if (this.relatedProductId && this.relatedProductId !== 'none') {
        const relatedOption = document.querySelector(`option[value="${this.relatedProductId}"]`);
        const parts = this.relatedProductId.split('/');
        const relatedId = parseInt(parts[parts.length - 1], 10);
        const relatedPriceCents = parseInt(relatedOption?.dataset?.price ?? '0', 10) || 0;

        // Get the formatted price from the map (set by Liquid with currency conversion)
        // This is the source of truth since it's already formatted with the correct currency
        const relatedPriceFromMap = window.HMR?.prices?.get(relatedId);
        const additionalPrice = relatedPriceFromMap || formatPrice(relatedPriceCents, relatedId);
        const isAdditionalPriceZero = isPriceZero(additionalPrice.replace(' ', ''));

        // biome-ignore lint: debugging price display
        console.log('[product-sodium] Additional price debugging:', {
          relatedId,
          relatedPriceCents,
          relatedPriceFromMap,
          additionalPrice,
          isAdditionalPriceZero,
        });

        // Only show additional price if it's not zero
        formattedAdditionalPrice = isAdditionalPriceZero ? '' : ` + ${additionalPrice}`;
      }

      const newPrice =
        mainPriceDisplay || formattedAdditionalPrice
          ? `<span>${mainPriceDisplay}${formattedAdditionalPrice}</span>`
          : '';

      // biome-ignore lint: debugging price display
      console.log('[product-sodium] Final price display:', {
        mainPriceDisplay,
        formattedAdditionalPrice,
        newPrice,
        willDisplay: !!newPrice,
      });

      buttonPrice.innerHTML = newPrice;

      // Update the buttons
      const comingSoonButton = this.querySelector('.coming-soon-button');
      const inquireButton = this.querySelector('button.inquire-button');

      if (isComingSoon) {
        const date = comingSoonButton.querySelector('.product-release-date');
        date.innerHTML = releaseDate ?? '';
        turnOnButton(comingSoonButton);
        turnOffButton(this.addToBagButton);
        turnOffButton(inquireButton);
      } else if (window.PRODUCT.inquiriesOnly) {
        turnOnButton(inquireButton);
        turnOffButton(this.addToBagButton);
        turnOffButton(comingSoonButton);
      } else {
        // Always show the add-to-bag button, but update its content based on availability
        turnOnButton(this.addToBagButton);
        turnOffButton(comingSoonButton);
        turnOffButton(inquireButton);

        if (available && !soldOut) {
          this.addToBagButton.removeAttribute('data-bis');
          const addToCartLabel = window.PRODUCT?.metafields?.hmr?.add_to_cart_label || ADD_TO_CART;
          this.addToBagButton.querySelector('div').innerHTML = addToCartLabel;
          this.addToBagButton.setAttribute(
            'aria-label',
            `Click to add ${window.PRODUCT?.title || 'this product'} to your bag`,
          );
        } else {
          this.addToBagButton.setAttribute('data-bis', 'true');
          const backInStockLabel = window.PRODUCT?.metafields?.hmr?.back_in_stock_label;
          this.addToBagButton.querySelector('div').innerHTML = backInStockLabel;
          this.addToBagButton.setAttribute(
            'aria-label',
            `Click to sign up for back in stock notifications for ${window.PRODUCT?.title || 'this product'}`,
          );
        }
      }
    }

    document.querySelector('.product-sku').innerHTML = sku || '';
    document.querySelector('.product-material').innerHTML = material || '';
    document.querySelector('.product-dimensions').innerHTML = dimensions || '';
    // Set the URL
    const url = new URL(window.location.href);
    url.searchParams.set('variant', id);
    window.history.replaceState({}, '', url.toString());

    // Update the gradient background
    const gradientBackground = this.querySelector('.product-color-gradient');
    if (gradientColors && gradientColors.length > 0) {
      gradientBackground.style.background = `linear-gradient(to right, ${gradientColors.join(', ')})`;
    } else {
      gradientBackground.style.background = hexColor;
    }

    // Update the stones
    const stoneNodes = [...this.querySelectorAll('.product-stones')];
    if (stoneNodes.length > 0 && diamonds.length > 0) {
      let idx = 0;
      for (const stonesNode of stoneNodes) {
        const diamond = diamonds[idx];
        const image = diamond.image;
        const numberOfStones = diamond.number;
        const newStone = document.createElement('div');

        // Use mm attribute if available for width, and scale height proportionally
        if (diamond.mm) {
          const widthPx = convertMmToPx(diamond.mm);
          const scale = widthPx / image.width;
          const heightPx = image.height * scale;
          newStone.style.width = `${widthPx}px`;
          newStone.style.height = `${heightPx}px`;
        } else {
          newStone.style.height = `${image.height / 2}px`;
          newStone.style.width = `${image.width / 2}px`;
        }
        newStone.innerHTML = image.src;
        // remove the children of the stonesNode
        while (stonesNode.firstChild) {
          stonesNode.removeChild(stonesNode.firstChild);
        }
        for (let i = 0; i < numberOfStones; i++) {
          const stoneClone = newStone.cloneNode(true);
          stonesNode.appendChild(stoneClone);
        }
        addClass(stonesNode, ON);
        idx++;
      }
    } else if (stoneNodes.length > 0) {
      // If there are no diamonds, clear the stones
      stoneNodes.forEach((stonesNode) => {
        while (stonesNode.firstChild) {
          stonesNode.removeChild(stonesNode.firstChild);
        }
      });
    }

    // Update any gradient-stones components
    const gradientStonesElements = this.querySelectorAll('gradient-stones');
    if (gradientStonesElements.length > 0 && diamonds.length > 0) {
      gradientStonesElements.forEach((element, idx) => {
        const diamond = diamonds[idx];
        if (!diamond) {
          return;
        }

        const { image, number: numberOfStones, mm } = diamond;

        // Force a complete refresh of the stones visualization
        element.dataset.src = image.src;
        element.dataset.height = image.height.toString();
        element.dataset.width = image.width.toString();
        element.dataset.count = numberOfStones.toString();

        if (mm) {
          element.dataset.mm = mm.toString();
        } else {
          element.removeAttribute('data-mm');
        }

        element.calculateDimensions();
        element.clearStones();
        element.updateMaskDimensions();
        element.renderStones();
      });
    } else if (gradientStonesElements.length > 0) {
      // If there are no diamonds, clear the stones
      gradientStonesElements.forEach((element) => {
        element.clearStones();
      });
    }

    // Update the background image
    const backgroundImage = document.querySelector('.product-media-image,.product-media-image-below');

    if (window.SECONDARY_PRODUCTS?.length > 0) {
      const option = document.querySelector('option[value="' + this.relatedProductId + '"]');

      if (!option || option.value === 'none' || option.dataset.productId === 'none') {
        backgroundImage.innerHTML = '';
        const player = document.querySelector('.product-media-video');
        removeClass(player, ON);
        return;
      }
      const productId = parseInt(option.dataset.productId, 10);
      const secondaryProduct = window.SECONDARY_PRODUCTS.find((product) => product.id === productId);

      if (secondaryProduct && secondaryProduct.images.length > 0) {
        // Find the image that matches the selected variant title, color, and product ID
        // All images will have variant titles, so match on all three criteria
        const selectedImage = secondaryProduct.images.find((image) => {
          const colorMatch = image.productColor === this.variant.color;
          const productIdMatch = image.productId === productId;
          const titleMatch =
            image.variantTitle &&
            option.dataset.variantTitle &&
            option.dataset.variantTitle.startsWith(image.variantTitle);

          return colorMatch && titleMatch && productIdMatch;
        });

        if (selectedImage) {
          backgroundImage.innerHTML = selectedImage.backgroundImage;
          playVideo({ media: selectedImage });
        } else {
          backgroundImage.innerHTML = '';
        }
      } else {
        backgroundImage.innerHTML = '';
      }
    } else if (window.ADDITIONAL_PRODUCTS?.length > 0) {
      const option = document.querySelector('option[value="' + this.relatedProductId + '"]');
      if (option.value === 'none') {
        backgroundImage.innerHTML = '';
        const player = document.querySelector('.product-media-video');
        removeClass(player, ON);
        return;
      }
      const color = option.dataset.color;
      const productId = parseInt(option.dataset.productId, 10);
      const additionalProduct = window.ADDITIONAL_PRODUCTS.find((product) => product.id === productId);

      let image = null;
      // If the product has media, find the media for the selected color and variant
      if (additionalProduct.media.length > 0) {
        const additionalProductVariantId = parseInt(option.value.split('/').pop(), 10);
        let additionalProductMedia = additionalProduct.media.find(
          ({ color, title, variantId }) =>
            color === this.variant.color &&
            variantId === additionalProductVariantId &&
            title.indexOf(window.PRODUCT.title) > -1,
        );
        if (!additionalProductMedia) {
          additionalProductMedia = additionalProduct.media.find(
            ({ color, variantId }) => color === this.variant.color && variantId === additionalProductVariantId,
          );
        }
        backgroundImage.innerHTML = additionalProductMedia ? additionalProductMedia.backgroundImage : '';
        playVideo({ media: additionalProductMedia });
      } else {
        // If no media, find the image with the matching color and variant in the alt tag
        for (const img of additionalProduct.images) {
          if (img.alt === `${this.variant.color} ${color}`) {
            image = img;
            break;
          }
        }
        backgroundImage.innerHTML = image ? image.src : '';
        // Update the video
        const mediaVariant = window.VARIANTS.find((variant) => variant.id === id);
        playVideo(mediaVariant);
      }
    } else {
      // Update the video
      const mediaVariant = window.VARIANTS.find((variant) => variant.id === id);
      playVideo(mediaVariant);
      if (media?.backgroundImage) {
        backgroundImage.innerHTML = media.backgroundImage;
      }
    }

    this.hideCarouselColor();

    setTimeout(this.handleResize, 100);
  }

  setBackgroundHeight() {
    const productTop = document.querySelector('.product-top');
    const productTopBackground = document.querySelector('.product-top-background');
    if (productTop && productTopBackground) {
      const productTopHeight = productTop.offsetHeight;
      productTopBackground.style.height = `${productTopHeight}px`;
    }
  }

  updateLabelHeights() {
    const mobile = isMobile();
    const resizeElements = this.querySelectorAll(`.resize-element${mobile ? '' : ':not(.mobile)'}`);
    let index = 0;
    for (const label of this.labels) {
      const option = resizeElements[index];
      // Get the corresponding option element
      if (option) {
        // Use offsetHeight for more reliable measurement
        const optionHeight = option.offsetHeight;
        label.style.height = `${optionHeight}px`;
      }
      index++;
    }
  }
}

class GradientStones extends HTMLElement {
  constructor() {
    super();
    this.renderStones = this.renderStones.bind(this);
    this.handleResize = debounce(() => {
      this.updateContainerWidth();
    }, 200);
  }

  connectedCallback() {
    this.id = this.getAttribute('id');
    this.wrapper = this.querySelector('.gradient-wrapper');
    this.maskGroup = this.querySelector(`#${this.id}-mask`);
    this.overlayGroup = this.querySelector(`#${this.id}-overlay`);
    this.diamondSvg = this.querySelector('.diamond-svg');
    this.diamondMask = this.querySelector('#diamond-mask');
    this.src = this.dataset.src;
    this.count = parseInt(this.dataset.count, 10);
    this.height = 0;
    this.width = 0;
    this.containerWidth = this.offsetWidth;

    // Initialize mask dimensions
    this.calculateDimensions();
    this.updateMaskDimensions();
    this.renderStones();

    // Add resize listener to update layout when window size changes
    window.addEventListener('resize', this.handleResize);

    // Add mutation observer to listen for changes to the data-count attribute
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-count') {
          const newCount = parseInt(this.dataset.count, 10);
          this.updateStoneCount(newCount);
        }
      });
    });
    this.observer.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    // Clean up resize listener and mutation observer when element is removed
    window.removeEventListener('resize', this.handleResize);
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  calculateDimensions() {
    const originalWidth = parseInt(this.dataset.width, 10);
    const originalHeight = parseInt(this.dataset.height, 10);

    if (this.dataset.mm && originalWidth) {
      const mm = parseFloat(this.dataset.mm);
      const newWidth = convertMmToPx(mm);
      const scale = newWidth / originalWidth;
      this.width = newWidth * 0.75;
      this.height = originalHeight * scale * 0.75;
    } else {
      this.width = originalWidth || 0;
      this.height = originalHeight || 0;
    }
  }

  updateContainerWidth() {
    const newWidth = this.offsetWidth;
    if (newWidth !== this.containerWidth) {
      this.containerWidth = newWidth;
      this.clearStones();
      this.calculateDimensions();
      this.updateMaskDimensions();
      this.renderStones();
    }
  }

  updateMaskDimensions() {
    const gapX = 3;
    const gapY = 2;

    // Calculate how many stones fit per row
    const stonesPerRow = Math.max(1, Math.floor(this.containerWidth / (this.width + gapX)));
    const rows = Math.ceil(this.count / stonesPerRow);
    const totalHeight = rows * (this.height + gapY) - gapY; // Subtract last gap

    // Update SVG dimensions
    if (this.diamondSvg) {
      this.diamondSvg.setAttribute('height', totalHeight);
      this.diamondSvg.setAttribute('width', this.containerWidth);
    }

    // Update mask dimensions
    if (this.diamondMask) {
      this.diamondMask.setAttribute('width', this.containerWidth);
      this.diamondMask.setAttribute('height', totalHeight);
    }

    // Update overlay SVG dimensions
    const overlaySvg = this.querySelector('.overlay-svg');
    if (overlaySvg) {
      overlaySvg.setAttribute('width', this.containerWidth);
      overlaySvg.setAttribute('height', totalHeight);
      overlaySvg.setAttribute('viewBox', `0 0 ${this.containerWidth} ${totalHeight}`);
    }

    // Ensure the wrapper has the right dimensions
    if (this.wrapper) {
      this.wrapper.style.height = `${totalHeight}px`;
    }
  }

  clearStones() {
    // Remove all existing stones
    while (this.maskGroup.firstChild) {
      this.maskGroup.removeChild(this.maskGroup.firstChild);
    }
    while (this.overlayGroup.firstChild) {
      this.overlayGroup.removeChild(this.overlayGroup.firstChild);
    }
  }

  updateStoneCount(count) {
    if (count !== this.count) {
      this.count = count;
      // Also update the data attribute to ensure consistency
      this.dataset.count = count.toString();
      this.clearStones();
      this.calculateDimensions();
      this.updateMaskDimensions();
      this.renderStones();
    }
  }

  renderStones() {
    const gapX = 3;
    const gapY = 2;

    // Update the alignment of stones to be right-aligned
    let x = 0;
    let y = 0;
    let maxY = 0;

    // Calculate how many stones can fit in a row
    const stonesPerRow = Math.max(1, Math.floor(this.containerWidth / (this.width + gapX)));
    const rows = Math.ceil(this.count / stonesPerRow);
    const totalHeight = rows * (this.height + gapY) - gapY;

    // Set the SVG and mask heights before creating stones
    if (this.diamondSvg) {
      this.diamondSvg.setAttribute('height', totalHeight);
    }

    if (this.diamondMask) {
      this.diamondMask.setAttribute('width', this.containerWidth);
      this.diamondMask.setAttribute('height', totalHeight);
    }

    // Create stones in a grid layout
    for (let i = 0; i < this.count; i++) {
      // Calculate position with fixed width per row
      const row = Math.floor(i / stonesPerRow);
      const col = i % stonesPerRow;

      x = this.containerWidth - (col + 1) * (this.width + gapX);
      y = row * (this.height + gapY);

      // Track the maximum y position for height calculation
      maxY = Math.max(maxY, y + this.height);

      // Create mask image
      const maskImage = document.createElementNS('http://www.w3.org/2000/svg', 'image');
      maskImage.setAttribute('href', this.src);
      maskImage.setAttribute('x', x);
      maskImage.setAttribute('y', y);
      maskImage.setAttribute('width', this.width);
      maskImage.setAttribute('height', this.height);
      this.maskGroup.appendChild(maskImage);

      // Create overlay image
      const overlayImage = document.createElementNS('http://www.w3.org/2000/svg', 'image');
      overlayImage.setAttribute('href', this.src);
      overlayImage.setAttribute('x', x);
      overlayImage.setAttribute('y', y);
      overlayImage.setAttribute('width', this.width);
      overlayImage.setAttribute('height', this.height);
      this.overlayGroup.appendChild(overlayImage);
    }

    // Update the wrapper height to match the calculated height
    this.wrapper.style.height = `${maxY}px`;

    // Make sure the overlay SVG dimensions match the content
    const overlaySvg = this.querySelector('.overlay-svg');
    if (overlaySvg) {
      overlaySvg.setAttribute('width', this.containerWidth);
      overlaySvg.setAttribute('height', maxY);
      overlaySvg.setAttribute('viewBox', `0 0 ${this.containerWidth} ${maxY}`);
    }
  }

  updateGradientBackground(gradientColors, hexColor) {
    const maskedGradientBackground = this.querySelector('.masked-gradient');
    if (maskedGradientBackground) {
      if (gradientColors && gradientColors.length > 0) {
        maskedGradientBackground.style.background = `linear-gradient(to right, ${gradientColors.join(', ')})`;
      } else {
        maskedGradientBackground.style.background = hexColor;
      }
    }
  }
}

if (!customElements.get('product-sodium')) {
  customElements.define('product-sodium', ProductSodium);
}

if (!customElements.get('gradient-stones')) {
  customElements.define('gradient-stones', GradientStones);
}

// Add the preload functions
const preloadQueue = new Set();
const preloadImage = () => {
  if (preloadQueue.size === 0) {
    return;
  }

  const div = document.createElement('div');
  const image = preloadQueue.values().next().value;
  div.innerHTML = image;
  const img = div.querySelector('img');
  img.style.display = 'block';
  img.style.height = 'auto';
  img.style.width = '100vw';
  img.style.position = 'absolute';
  img.style.opacity = '0';
  img.style.pointerEvents = 'none';
  img.style.zIndex = '-1';
  img.style.top = '0';
  img.style.left = '0';
  img.onload = () => {
    preloadQueue.delete(image);
    preloadImage();
  };
  img.setAttribute('loading', 'eager');
  document.body.appendChild(img);
};

const preloadVariantBackgroundImages = () => {
  const { ADDITIONAL_PRODUCTS, VARIANTS } = window;
  if (ADDITIONAL_PRODUCTS?.length > 0) {
    for (const product of ADDITIONAL_PRODUCTS) {
      for (const image of product.images) {
        preloadQueue.add(image.src);
      }
      for (const media of product.media) {
        if (media.backgroundImage) {
          preloadQueue.add(media.backgroundImage);
        }
      }
    }
  }
  for (const key in Object.entries(VARIANTS)) {
    const variant = VARIANTS[key];
    if (!variant.media?.backgroundImage) {
      continue;
    }
    preloadQueue.add(variant.media.backgroundImage);
  }

  preloadImage();
};

preloadVariantBackgroundImages();
